import { AsyncPipe, LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import type { AbstractControl, NonNullableFormBuilder } from '@angular/forms';
import { isFormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxOption, OnyxOptional, OnyxTab } from '@onyx/angular';
import {
  FormHelper,
  OnyxDropdownComponent,
  OnyxFormMode,
  OnyxIconComponent,
  OnyxLanguagePipe,
  OnyxMessageComponent,
  onyxMinNumberValidator,
  OnyxModalService,
  OnyxSectionComponent,
  OnyxTabsComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxToggleComponent,
  OnyxTooltipComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import {
  chain,
  difference,
  intersection,
  isArray,
  isBoolean,
  isObject,
  uniq,
} from 'lodash';
import type { Observable } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs';
import { FLEET_BASE_PARAMETERS_KEYS } from '../../../../dashboard/fleet/common/constants/fleet-base-parameters-keys';
import { SemiTrailerSize } from '../../../../dashboard/fleet/common/enums/semi-trailer-size';
import { TrailerSize } from '../../../../dashboard/fleet/common/enums/trailer-size';
import { CompanyDictionaryCode } from '../../../../dashboard/management-panel/dictionaries/common/enums/company-dictionary-code';
import type { CompanyDriverPermission } from '../../../../dashboard/management-panel/dictionaries/common/interfaces/company-driver-permission';
import { CompanyDictionariesService } from '../../../../dashboard/management-panel/dictionaries/common/services/company-dictionaries.service';
import type { DictionariesDriverPermissionModalData } from '../../../../dashboard/management-panel/dictionaries/dictionaries/dictionaries-driver-permissions/dictionaries-driver-permission-modal/dictionaries-driver-permission-modal.component';
import { DictionariesDriverPermissionModalComponent } from '../../../../dashboard/management-panel/dictionaries/dictionaries/dictionaries-driver-permissions/dictionaries-driver-permission-modal/dictionaries-driver-permission-modal.component';
import type { DictionariesVehicleParameterModalData } from '../../../../dashboard/management-panel/dictionaries/dictionaries/dictionaries-vehicle-parameters/dictionaries-vehicle-parameter-modal/dictionaries-vehicle-parameter-modal.component';
import { DictionariesVehicleParameterModalComponent } from '../../../../dashboard/management-panel/dictionaries/dictionaries/dictionaries-vehicle-parameters/dictionaries-vehicle-parameter-modal/dictionaries-vehicle-parameter-modal.component';
import { ORDER_BASE_PARAMETERS_KEYS } from '../../../../dashboard/orders/common/constants/order-base-parameters-keys';
import type { AdrClass } from '../../../enums/adr-class';
import { DictionaryCode } from '../../../enums/dictionary-code';
import type { FuelType } from '../../../enums/fuel-type';
import { ValidationHelper } from '../../../helpers/validation.helper';
import type {
  FleetType,
  FleetValidationByType,
} from '../../../interfaces/validation/fleet-validation';
import type { ForbiddenField } from '../../../interfaces/validation/validation-field';
import type { ValidationGroup } from '../../../interfaces/validation/validation-group';
import { DictionariesService } from '../../../services/dictionaries.service';
import { UnitWarningPipe } from '../../../pipes/unit-warning.pipe';

export type FleetAdditionalParametersFormGroup = ReturnType<
  typeof ParametersFormComponent.buildForm<'fleet'>
>;
export type OrderParametersFormGroup = ReturnType<
  typeof ParametersFormComponent.buildForm<'order'>
>;

@Component({
  selector: 'app-parameters-form',
  imports: [
    UnitWarningPipe,
    OnyxSectionComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxIconComponent,
    OnyxToggleComponent,
    OnyxDropdownComponent,
    AsyncPipe,
    OnyxTextFieldComponent,
    OnyxTabsComponent,
    OnyxTooltipComponent,
    OnyxTooltipDirective,
    NgTemplateOutlet,
    OnyxMessageComponent,
    NgTemplateOutlet,
    LowerCasePipe,
  ],
  templateUrl: './parameters-form.component.html',
  styleUrl: './parameters-form.component.scss',
})
export class ParametersFormComponent<
  T extends 'fleet' | 'order',
> implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(OnyxModalService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslateService);

  public readonly type = input.required<T>();
  public readonly form =
    input.required<
      T extends 'fleet'
        ? FleetAdditionalParametersFormGroup
        : OrderParametersFormGroup
    >();
  public readonly validation = input.required<ValidationGroup | undefined>();
  public readonly fleetValidation =
    input<OnyxOptional<FleetValidationByType>>();
  public readonly vehicleTypes = input<FleetType[]>();
  public readonly dischargeTypes = input<string[]>();
  public readonly readonly = input<boolean>(false);
  public readonly hasAssignedFleet = input<boolean>(false);

  public readonly compatibleVehicleTypesChange = output<FleetType[] | null>();

  protected readonly I18N = 'forms.parameters';

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly SemiTrailerSize = SemiTrailerSize;
  protected readonly TrailerSize = TrailerSize;

  protected readonly languages$ = this.dictionariesService.getDictionary(
    DictionaryCode.LANGUAGE,
  );
  protected readonly semiTrailerSizes$ = this.dictionariesService
    .getDictionary(DictionaryCode.SEMI_TRAILER_SIZE)
    .pipe(
      map((options) =>
        options.map((option): OnyxTab<string> => ({
          value: option.value,
          icon: {
            name: `semi-trailer-${option.value}-outlined`,
            size: 20,
          },
          tooltip: option.name,
        })),
      ),
    );
  protected readonly trailerSizes$ = this.dictionariesService
    .getDictionary(DictionaryCode.TRAILER_SIZE)
    .pipe(
      map((options) =>
        options.map((option): OnyxTab<string> => ({
          value: option.value,
          icon: {
            name: `${option.value}-filled`,
            size: 20,
          },
          tooltip: option.name,
        })),
      ),
    );
  protected readonly vehicleParameters$ = this.getDictionaryParameters(
    CompanyDictionaryCode.VEHICLE_PARAMETER,
  );
  protected readonly driverPermissions$ = this.getDictionaryParameters(
    CompanyDictionaryCode.DRIVER_PERMISSION,
  );
  protected readonly adrClasses$ = this.dictionariesService.getDictionary(
    DictionaryCode.ADR_CLASS,
  );
  protected readonly emissionStandards$ =
    this.dictionariesService.getDictionary(DictionaryCode.EMISSION_STANDARD);
  protected readonly fuelTypes$ = this.dictionariesService.getDictionary(
    DictionaryCode.FUEL_TYPE,
  );

  private readonly DEFAULT_TEMPLATE_ORDER = {
    'labels.adr': 0,
    'labels.safeParking': 1,
    'labels.emissionStandard': 16,
    'labels.didb': 2,
    'labels.fuelTypes': 21,
    'labels.isExpress': 4,
    'labels.haccp': 6,
    'labels.hooks': 7,
    'labels.hdsCrane': 8,
    'labels.foreignLanguages': 10,
    'labels.customsSecuringRope': 26,
    'labels.mats': 12,
    'labels.coilWell': 3,
    'labels.exclusive': 14,
    'labels.loadingRamps': 15,
    'labels.sentNotification': 17,
    'labels.belts': 11,
    'labels.doubleCrew': 18,
    'labels.sanitaryInspection': 23,
    'labels.temperature': 25,
    'labels.semiTrailerSize': 22,
    'labels.trailerFor': 19,
    'labels.tailLift': 27,
    'labels.dumpContainer': 28,
    'labels.forklift': 29,
    'labels.palletExchange': 30,
  } as const;

  protected resetState = signal(true);

  protected order = computed(() => {
    const items = Object.keys(this.DEFAULT_TEMPLATE_ORDER).map((key) => ({
      key,
      label: this.translateService.instant(key),
    }));
    if (items.some(({ label, key }) => label === key)) {
      return this.DEFAULT_TEMPLATE_ORDER;
    }

    return chain(items)
      .orderBy(({ label }) => label, 'asc')
      .map(({ key }, index) => [key, index])
      .fromPairs()
      .value();
  });

  protected i18n = computed(
    () =>
      ({
        fleet: 'fleet.fleetForm.additionalParameters',
        order: 'orders.orderForm.parameters',
      })[this.type()],
  );

  protected vehicleTypesCompatibility = computed(() => {
    const fleetValidation = this.fleetValidation();
    const vehicleTypes = this.vehicleTypes();
    const dischargeTypes = this.dischargeTypes();
    if (!fleetValidation || !vehicleTypes || !dischargeTypes) return null;

    const value = this.value();

    const activeParameters = chain(value)
      .keys()
      .filter(
        (parameter) =>
          !FLEET_BASE_PARAMETERS_KEYS.includes(parameter as any) &&
          !ORDER_BASE_PARAMETERS_KEYS.includes(parameter as any) &&
          ParametersFormComponent.hasValue(
            value[parameter as keyof typeof value],
          ),
      )
      .value();
    const conflictParameters = activeParameters
      .map(
        (parameter) =>
          [
            parameter,
            vehicleTypes.filter(
              (vehicleType) =>
                fleetValidation[vehicleType]['additionalParameters'].fields[
                  parameter
                ].type !== 'forbidden',
            ),
          ] as const,
      )
      .filter(
        ([_, compatibleTypes]) =>
          compatibleTypes.length !== vehicleTypes.length,
      );

    const allowedDischargeTypes = vehicleTypes.map((vehicleType) => {
      const dischargeTypesField =
        fleetValidation[vehicleType]['cargoSpace']?.fields['dischargeTypes'];
      const allowedDischargeTypes: string[] =
        dischargeTypesField && 'values' in dischargeTypesField
          ? dischargeTypesField.values
          : [];
      return [vehicleType, allowedDischargeTypes] as const;
    });
    const conflictDischargeTypes = dischargeTypes.map(
      (dischargeType) =>
        [
          dischargeType,
          allowedDischargeTypes
            .filter(([_, allowedDischargeTypes]) =>
              allowedDischargeTypes.includes(dischargeType),
            )
            .map(([vehicleType]) => vehicleType),
        ] as const,
    );

    if (!conflictParameters.length && !conflictDischargeTypes.length) {
      return null;
    }

    const conflictDischargeTypeVehicleTypes = uniq(
      conflictDischargeTypes.flatMap(([_, vehicleTypes]) => vehicleTypes),
    );
    const compatibleTypes = intersection(
      ...conflictParameters.map(([_, vehicleTypes]) => vehicleTypes),
      conflictDischargeTypeVehicleTypes,
    );
    if (compatibleTypes.length === vehicleTypes.length) return null;

    const hasDischargeTypesConflict =
      conflictDischargeTypeVehicleTypes.length !== vehicleTypes.length;
    const incompatibleTypes = difference(vehicleTypes, compatibleTypes);

    return {
      compatibleTypes,
      conflictParameters,
      conflictDischargeTypes: hasDischargeTypesConflict
        ? conflictDischargeTypes
        : [],
      incompatibleTypes,
    };
  });

  protected showAdrClasses = computed(() => {
    if (this.type() === 'order') return true;

    const field = this.validation()?.fields['adrClasses'];
    return field && field.type !== 'forbidden';
  });
  protected hasAdrClasses = signal(false);

  protected hasMinEmissionStandard = signal(false);

  protected hasFuelTypes = signal(false);

  protected showHooksNumber = computed(() => {
    const field = this.validation()?.fields['hooksNumber'];
    return !!field && field.type !== 'forbidden';
  });
  protected hasHooksNumber = signal(true);

  protected hasDriverLanguages = signal(false);

  protected showMatsNumber = computed(() => {
    const field = this.validation()?.fields['matsNumber'];
    return !!field && field.type !== 'forbidden';
  });
  protected hasMatsNumber = signal(false);

  protected showCoilWellLength = computed(() => {
    const field = this.validation()?.fields['coilWellLength'];
    return !!field && field.type !== 'forbidden';
  });
  protected hasCoilWellLength = signal(false);

  protected hasSentNotificationNumber = signal(false);

  protected showBeltsNumber = computed(() => {
    const field = this.validation()?.fields['beltsNumber'];
    return !!field && field.type !== 'forbidden';
  });
  protected hasBeltsNumber = signal(false);

  protected showTemperatureRange = computed(() => {
    const field = this.validation()?.fields['temperatureRange'];
    return !!field && field.type !== 'forbidden';
  });
  protected hasTemperatureRange = signal(true);

  protected showSemiTrailerSize = computed(() => {
    const field = this.validation()?.fields['semiTrailerSize'];
    return !!field && field.type !== 'forbidden';
  });

  protected showTrailerSize = computed(() => {
    const field = this.validation()?.fields['trailerSize'];
    return !!field && field.type !== 'forbidden';
  });

  private value!: Signal<
    ReturnType<ReturnType<typeof this.form>['getRawValue']>
  >;

  public ngOnInit(): void {
    const controls = this.form().controls;
    this.reset();

    this.value = toSignal(
      this.form().valueChanges.pipe(map(() => this.form().getRawValue())),
      { initialValue: this.form().getRawValue(), injector: this.injector },
    ) as typeof this.value;

    effect(
      () =>
        this.compatibleVehicleTypesChange.emit(
          this.vehicleTypesCompatibility()?.compatibleTypes ?? null,
        ),
      { injector: this.injector },
    );

    effect(
      () => {
        const FORBIDDEN_FIELD: ForbiddenField = { type: 'forbidden' } as const;

        const enabledAdrClasses = this.hasAdrClasses();
        const enabledHooksNumber =
          this.showHooksNumber() && this.hasHooksNumber();
        const enabledMatsNumber = this.hasMatsNumber();
        const enabledCoilWellLength = this.hasCoilWellLength();
        const enabledBeltsNumber = this.hasBeltsNumber();
        const enabledTemperatureRange =
          this.showTemperatureRange() && this.hasTemperatureRange();

        // Fleet
        const hasAssignedFleet = this.hasAssignedFleet();

        // Order
        const enabledMinEmissionStandard = this.hasMinEmissionStandard();
        const enabledFuelTypes = this.hasFuelTypes();
        const enabledDriverLanguages = this.hasDriverLanguages();
        const enabledSentNotificationNumber = this.hasSentNotificationNumber();

        const fields = this.validation()?.fields;
        const reset = this.resetState();

        setTimeout(() => {
          ValidationHelper.toggleControls(controls.adrClasses, {
            enabled: enabledAdrClasses,
            reset,
          });
          ValidationHelper.updateControl(
            controls.hasHaccp,
            fields?.['hasHaccp'] ?? FORBIDDEN_FIELD,
          );
          ValidationHelper.updateControl(
            controls.hasHdsCrane,
            fields?.['hasHdsCrane'] ?? FORBIDDEN_FIELD,
          );
          ValidationHelper.toggleControls(controls.hooksNumber, {
            enabled: enabledHooksNumber,
            reset,
          });
          ValidationHelper.updateControl(
            controls.hasCustomsSecuringRope,
            fields?.['hasCustomsSecuringRope'] ?? FORBIDDEN_FIELD,
          );
          ValidationHelper.toggleControls(controls.matsNumber, {
            enabled: enabledMatsNumber,
            reset,
          });
          ValidationHelper.updateControl(
            controls.hasLoadingsRamps,
            fields?.['hasLoadingsRamps'] ?? FORBIDDEN_FIELD,
          );
          ValidationHelper.toggleControls(controls.beltsNumber, {
            enabled: enabledBeltsNumber,
            reset,
          });
          ValidationHelper.updateControl(
            controls.hasSanitaryInspection,
            fields?.['hasSanitaryInspection'] ?? FORBIDDEN_FIELD,
          );
          ValidationHelper.toggleControls(controls.temperatureRange, {
            enabled: enabledTemperatureRange,
            reset,
          });
          ValidationHelper.updateControl(
            controls.hasTailLift,
            fields?.['hasTailLift'] ?? FORBIDDEN_FIELD,
          );
          ValidationHelper.updateControl(
            controls.hasDumpContainer,
            fields?.['hasDumpContainer'] ?? FORBIDDEN_FIELD,
          );
          ValidationHelper.updateControl(
            controls.hasForklift,
            fields?.['hasForklift'] ?? FORBIDDEN_FIELD,
          );

          // Fleet
          if (controls.semiTrailerSize) {
            ValidationHelper.updateControl(
              controls.semiTrailerSize,
              fields?.['semiTrailerSize'] ?? FORBIDDEN_FIELD,
            );
            ValidationHelper.toggleControls(controls.semiTrailerSize, {
              enabled: !hasAssignedFleet,
              reset: false,
            });
          }
          if (controls.trailerSize) {
            ValidationHelper.updateControl(
              controls.trailerSize,
              fields?.['trailerSize'] ?? FORBIDDEN_FIELD,
            );
            ValidationHelper.toggleControls(controls.trailerSize, {
              enabled: !hasAssignedFleet,
              reset: false,
            });
          }
          if (controls.coilWellLength) {
            ValidationHelper.toggleControls(controls.coilWellLength, {
              enabled: enabledCoilWellLength,
              reset,
            });
          }

          // Order
          if (controls.minEmissionStandard) {
            ValidationHelper.toggleControls(controls.minEmissionStandard, {
              enabled: enabledMinEmissionStandard,
              reset,
            });
          }
          if (controls.fuelTypes) {
            ValidationHelper.toggleControls(controls.fuelTypes, {
              enabled: enabledFuelTypes,
              reset,
            });
          }
          if (controls.driverLanguages) {
            ValidationHelper.toggleControls(controls.driverLanguages, {
              enabled: enabledDriverLanguages,
              reset,
            });
          }
          if (controls.sentNotificationNumber) {
            ValidationHelper.toggleControls(controls.sentNotificationNumber, {
              enabled: enabledSentNotificationNumber,
              reset,
            });
          }

          this.resetState.set(true);
          this.changeDetectorRef.detectChanges();
        });
      },
      { injector: this.injector },
    );

    effect(
      () => {
        if (!this.readonly()) return;

        for (const control of Object.values(controls)) {
          if (!ParametersFormComponent.hasValue(control)) continue;
          setTimeout(() => control.disable({ emitEvent: false }));
        }
      },
      { injector: this.injector },
    );
  }

  public reset(reset = true): void {
    if (!reset) this.resetState.set(reset);

    const controls = this.form().controls;
    this.hasAdrClasses.set(
      ParametersFormComponent.hasValue(controls.adrClasses),
    );
    this.hasMinEmissionStandard.set(
      ParametersFormComponent.hasValue(controls.minEmissionStandard),
    );
    this.hasFuelTypes.set(ParametersFormComponent.hasValue(controls.fuelTypes));
    this.hasHooksNumber.update((hasHooksNumber) =>
      this.showHooksNumber()
        ? ParametersFormComponent.hasValue(controls.hooksNumber)
        : hasHooksNumber,
    );
    this.hasDriverLanguages.set(
      ParametersFormComponent.hasValue(controls.driverLanguages),
    );
    this.hasMatsNumber.set(
      ParametersFormComponent.hasValue(controls.matsNumber),
    );
    this.hasCoilWellLength.set(
      ParametersFormComponent.hasValue(controls.coilWellLength),
    );
    this.hasSentNotificationNumber.set(
      ParametersFormComponent.hasValue(controls.sentNotificationNumber),
    );
    this.hasBeltsNumber.set(
      ParametersFormComponent.hasValue(controls.beltsNumber),
    );
    this.hasTemperatureRange.update((hasTemperatureRange) =>
      this.showTemperatureRange()
        ? ParametersFormComponent.hasValue(controls.temperatureRange)
        : hasTemperatureRange,
    );
  }

  protected addVehicleParameter(name?: string): void {
    this.modalService.open<DictionariesVehicleParameterModalData>(
      DictionariesVehicleParameterModalComponent,
      { mode: OnyxFormMode.ADD, name },
    );
  }

  protected addDriverPermission(name?: string): void {
    this.modalService.open<DictionariesDriverPermissionModalData>(
      DictionariesDriverPermissionModalComponent,
      { mode: OnyxFormMode.ADD, name },
    );
  }

  protected shouldDisplayControl(
    control: AbstractControl | null | undefined,
    options: { conditional: boolean } = { conditional: false },
  ): boolean | undefined {
    if (!this.readonly()) return options.conditional || control?.enabled;
    return ParametersFormComponent.hasValue(control);
  }

  private getDictionaryParameters(
    code: CompanyDictionaryCode,
  ): Observable<OnyxOption<string>[]> {
    return this.companyDictionariesService.reload$.pipe(
      startWith(undefined),
      switchMap(() =>
        this.companyDictionariesService.listDictionary(code).pipe(
          map((parameters) =>
            code === CompanyDictionaryCode.DRIVER_PERMISSION
              ? parameters.filter(
                  (parameter): parameter is CompanyDriverPermission =>
                    !('isDefault' in parameter && parameter.isDefault),
                )
              : parameters,
          ),
          map((parameters): OnyxOption<string>[] =>
            parameters.map((parameter) => ({
              value: parameter.uuid,
              name: this.languagePipe.transform(parameter.names),
            })),
          ),
        ),
      ),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public static buildForm<T>(type: T, fb: NonNullableFormBuilder) {
    return fb.group({
      adrClasses: fb.control<AdrClass[] | null>(null, [Validators.required]),
      hasHaccp: fb.control<boolean | null>(false),
      hasHdsCrane: fb.control<boolean | null>(false),
      hooksNumber: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(1),
      ]),
      hasCustomsSecuringRope: fb.control<boolean | null>(false),
      matsNumber: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(1),
      ]),
      hasLoadingsRamps: fb.control<boolean | null>(false),
      beltsNumber: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(1),
      ]),
      hasSanitaryInspection: fb.control<boolean | null>(false),
      temperatureRange: FormHelper.buildNumberRangeForm(fb),
      hasTailLift: fb.control<boolean | null>(false),
      hasDumpContainer: fb.control<boolean | null>(false),
      hasForklift: fb.control<boolean | null>(false),
      otherVehicleParameters: fb.control<string[] | null>(null),

      ...(type === 'fleet' && {
        isLowDeck: fb.control<boolean | null>(false),
        semiTrailerSize: fb.control<SemiTrailerSize | null>(null),
        trailerSize: fb.control<TrailerSize | null>(null),
        coilWellLength: fb.control<number | null>(null, [
          Validators.required,
          onyxMinNumberValidator(Number.EPSILON, 2),
        ]),
      }),

      ...(type === 'order' && {
        hasSafeParking: fb.control(false, [Validators.required]),
        isDedicated: fb.control(true, [Validators.required]),
        hasEcmr: fb.control(false, [Validators.required]),
        isExpress: fb.control(false, [Validators.required]),
        driverLanguages: fb.control<string[] | null>(null),
        isExclusive: fb.control(false, [Validators.required]),
        sentNotificationNumber: fb.control<string | null>(null, [
          Validators.required,
          onyxTextFieldLengthValidator,
        ]),
        hasDoubleCrew: fb.control(false, [Validators.required]),
        hasDoubleDeck: fb.control(false, [Validators.required]),
        hasPalletExchange: fb.control(false, [Validators.required]),
        requiresDidb: fb.control(false, [Validators.required]),
        minEmissionStandard: fb.control<string | null>(null, [
          Validators.required,
        ]),
        fuelTypes: fb.control<FuelType[] | null>(null, [Validators.required]),
        otherDriverPermissions: fb.control<string[] | null>(null),
      }),
    });
  }

  public static getDto<
    T = ReturnType<
      (
        FleetAdditionalParametersFormGroup | OrderParametersFormGroup
      )['getRawValue']
    >,
  >(form: T, validation: ValidationGroup): T {
    return chain(form)
      .entries()
      .map((entry) => ({ entry, field: validation.fields[entry[0]] }))
      .filter(
        ({ entry, field }) =>
          ORDER_BASE_PARAMETERS_KEYS.includes(entry[0] as any) || field != null,
      )
      .map(({ entry, field }) => {
        if (field?.type === 'forbidden') entry[1] = null;
        if (
          !isBoolean(entry[1]) &&
          !ParametersFormComponent.hasValue(entry[1])
        ) {
          entry[1] = null;
        }
        return entry;
      })
      .fromPairs()
      .value() as T;
  }

  private static hasValue(
    control: AbstractControl | any | null | undefined,
  ): boolean {
    const value = isFormControl(control) ? control.getRawValue() : control;
    if (value == null) return false;
    if (isBoolean(value)) return value;
    if (isArray(value)) return !!value.length;
    if (isObject(value)) {
      return Object.values(value).some((value) => value != null);
    }

    return true;
  }
}
